import React from "react";
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Avatar,
  Typography,
  TextField,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Button,
  useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { Computer, CloudDone, CloudOff } from "@mui/icons-material";
import { GlassDialog, dialogTransitionProps } from "../../../theme/theme";

export function CameraDialog({
  open,
  onClose,
  darkMode,
  newCamera,
  setNewCamera,
  addCamera,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <GlassDialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={fullScreen} {...dialogTransitionProps}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Avatar sx={{ bgcolor: alpha('#16A987', 0.1), width: 48, height: 48 }}>
            <Computer sx={{ color: '#16A987' }} />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>
              {newCamera.id ? "Edit Camera" : "Add New Camera"}
            </Typography>
            <Typography variant="caption" sx={{ color: darkMode ? '#93A2BC' : 'text.secondary' }}>
              {newCamera.id ? "Update camera information" : "Register a new camera"}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {newCamera.loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {newCamera.id ? (
              <>
                <TextField
                  fullWidth
                  label="System UID"
                  margin="normal"
                  value={newCamera.uuid}
                  disabled
                />
                <TextField
                  fullWidth
                  label="Camera Serial"
                  margin="normal"
                  value={newCamera.camera_serial}
                  onChange={(e) => setNewCamera({ ...newCamera, camera_serial: e.target.value })}
                  placeholder="Enter camera serial number"
                />
              </>
            ) : (
              <>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="camera-system-select-label">System UID</InputLabel>
                  <Select
                    labelId="camera-system-select-label"
                    value={newCamera.uuid}
                    onChange={(e) => setNewCamera({ ...newCamera, uuid: e.target.value })}
                    label="System UID"
                    renderValue={(selected) => {
                      const option = newCamera.systemOptions.find(opt => opt.value === selected);
                      return option ? (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>
                            {option.value.substring(0, 8)}...
                          </Typography>
                          <Chip size="small" label={option.project_id} color="primary" variant="outlined" />
                        </Box>
                      ) : '';
                    }}
                  >
                    <MenuItem value="" disabled>
                      <em>Select a System</em>
                    </MenuItem>
                    {newCamera.systemOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.5 }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>
                            {option.value}
                          </Typography>
                          <Typography variant="caption" sx={{ color: darkMode ? '#93A2BC' : 'text.secondary' }}>
                            Project: {option.project_id} • SW: {option.software_id}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="Camera Serial"
                  margin="normal"
                  value={newCamera.camera_serial}
                  onChange={(e) => setNewCamera({ ...newCamera, camera_serial: e.target.value })}
                  placeholder="Enter camera serial number"
                />
              </>
            )}

            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>
                Camera Status
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant={newCamera.status === "online" ? "contained" : "outlined"}
                  color="success"
                  onClick={() => setNewCamera({ ...newCamera, status: "online" })}
                  startIcon={<CloudDone />}
                  sx={{ flex: 1 }}
                >
                  Online
                </Button>
                <Button
                  variant={newCamera.status === "offline" ? "contained" : "outlined"}
                  color="error"
                  onClick={() => setNewCamera({ ...newCamera, status: "offline" })}
                  startIcon={<CloudOff />}
                  sx={{ flex: 1 }}
                >
                  Offline
                </Button>
              </Box>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button onClick={addCamera} variant="contained" sx={{ px: 4 }}>
          {newCamera.id ? "Update Camera" : "Add Camera"}
        </Button>
      </DialogActions>
    </GlassDialog>
  );
}

export default CameraDialog;
